// src/features/tasks/utils/taskStateUtils.js
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  AccessTime as AccessTimeIcon,
  HighlightOff as HighlightOffIcon,
} from '@mui/icons-material';
import { tokens } from '../../../theme';
import i18n from '../../../i18n';

/**
 * Get available state transitions based on current task state
 * This follows the state machine diagram for task transitions.
 * Quick-complete tasks skip IN_PROGRESS and are completed straight from PENDING.
 */
export const getAvailableTransitions = (task) => {
  const t = i18n.t.bind(i18n);
  const quickComplete = Boolean(task?.quick_complete);

  switch (task?.state) {
    case 'SCHEDULED':
      return []; // No transitions available for scheduled tasks
    case 'PENDING':
      if (quickComplete) {
        return [
          { action: 'complete', label: t('tasks:actions.complete_task'), icon: CheckCircleIcon, color: 'success' },
          { action: 'not_complete', label: t('tasks:actions.not_complete_task'), icon: HighlightOffIcon, color: 'warning' },
          { action: 'cancel', label: t('tasks:actions.cancel_task'), icon: StopIcon, color: 'error' },
        ];
      }
      return [
        { action: 'start', label: t('tasks:actions.start_task'), icon: PlayArrowIcon, color: 'success' },
        { action: 'not_complete', label: t('tasks:actions.not_complete_task'), icon: HighlightOffIcon, color: 'warning' },
        { action: 'cancel', label: t('tasks:actions.cancel_task'), icon: StopIcon, color: 'error' },
      ];
    case 'IN_PROGRESS':
      return [
        { action: 'complete', label: t('tasks:actions.complete_task'), icon: CheckCircleIcon, color: 'success' },
        { action: 'not_complete', label: t('tasks:actions.not_complete_task'), icon: HighlightOffIcon, color: 'warning' },
        { action: 'hold', label: t('tasks:actions.pause_task'), icon: PauseIcon, color: 'error' },
      ];
    case 'ON_HOLD':
      return [
        { action: 'resume_pending', label: t('tasks:actions.resume_task'), icon: RefreshIcon },
        ...(quickComplete ? [] : [
          { action: 'resume_progress', label: t('tasks:actions.start_task'), icon: PlayArrowIcon, color: 'success' },
        ]),
        { action: 'cancel', label: t('tasks:actions.cancel_task'), icon: StopIcon, color: 'error' },
      ];
    case 'COMPLETED':
      return [
        { action: 'reopen', label: t('tasks:actions.reopen_task'), icon: RefreshIcon },
      ];
    case 'NOT_COMPLETED':
      return [
        { action: 'reopen', label: t('tasks:actions.reopen_task'), icon: RefreshIcon },
      ];
    case 'CANCELLED':
      return [
        { action: 'reopen', label: t('tasks:actions.reopen_task'), icon: RefreshIcon },
      ];
    default:
      return [];
  }
};

/**
 * Configuration for different task states
 */
export const getStateConfig = (taskState) => {
  const t = i18n.t.bind(i18n);
  
  const STATE_CONFIG = {
    SCHEDULED: { color: 'info', icon: AccessTimeIcon, label: t('tasks:states.pending') },
    PENDING: { color: 'secondary', icon: ScheduleIcon, label: t('tasks:states.pending') },
    IN_PROGRESS: { color: 'info', icon: PlayArrowIcon, label: t('tasks:states.in_progress') },
    ON_HOLD: { color: 'warning', icon: PauseIcon, label: t('tasks:states.paused') },
    COMPLETED: { color: 'success', icon: CheckCircleIcon, label: t('tasks:states.completed') },
    NOT_COMPLETED: { color: 'warning', icon: HighlightOffIcon, label: t('tasks:states.not_completed') },
    CANCELLED: { color: 'error', icon: StopIcon, label: t('tasks:states.cancelled') },
  };
  
  return STATE_CONFIG[taskState] || STATE_CONFIG.PENDING;
};

/**
 * Get task color based on state and overdue status using theme tokens
 * @param {Object} task - The task object with state and due_date
 * @param {string} mode - Theme mode ('light' or 'dark')
 * @returns {string} - Hex color code from theme
 */
export const getTaskColor = (task, mode = 'light') => {
  const colors = tokens(mode);
  const { state, due_date } = task;
  
  // Check if task is overdue (highest priority)
  if (isTaskOverdue(task)) {
    return colors.redAccent[500]; // Red for overdue tasks
  }
  
  // State-based colors using theme tokens
  switch (state) {
    case 'COMPLETED':
      return "success.main";
    case 'NOT_COMPLETED':
      return "warning.main";
    case 'IN_PROGRESS':
      return "warning.main"; // Yellow for in progress tasks
    case 'PENDING':
      return "info.main"
    case 'ON_HOLD':
      return "secondary.main"; // Pink for on hold tasks
    case 'CANCELLED':
      return "error.light";
    default:
      return "grey.light"; // Default gray
  }
};

/**
 * Get lighter/darker shade of task color for hover effects
 * @param {Object} task - The task object
 * @param {string} mode - Theme mode ('light' or 'dark')
 * @param {string} intensity - 'lighter' or 'darker'
 * @returns {string} - Hex color code
 */
export const getTaskColorVariant = (task, mode = 'light', intensity = 'lighter') => {
  const colors = tokens(mode);
  const { state } = task;
  
  // Get the shade offset based on intensity
  const offset = intensity === 'lighter' ? -100 : 100;
  const baseShade = 500;
  const targetShade = Math.max(100, Math.min(900, baseShade + offset));
  
  if (isTaskOverdue(task)) {
    return colors.redAccent[targetShade];
  }
  
  switch (state) {
    case 'COMPLETED':
      return colors.greenAccent[targetShade];
    case 'NOT_COMPLETED':
      return colors.yellowAccent[targetShade];
    case 'IN_PROGRESS':
      return colors.yellowAccent[targetShade];
    case 'PENDING':
    case 'SCHEDULED':
      return colors.grey[targetShade];
    case 'ON_HOLD':
      return colors.pinkAccent[targetShade];
    case 'CANCELLED':
      return colors.grey[Math.max(100, Math.min(900, 600 + offset))];
    default:
      return colors.grey[targetShade];
  }
};

/**
 * Get text color that contrasts well with the task color using theme
 * @param {Object} task - The task object
 * @param {string} mode - Theme mode ('light' or 'dark')
 * @returns {string} - Hex color code for text from theme
 */
export const getTaskTextColor = (task, mode = 'light') => {
  const colors = tokens(mode);
  const backgroundColor = getTaskColor(task, mode);
  return colors.grey[1000];
  
  // Convert hex to RGB to calculate luminance
  const r = parseInt(backgroundColor.slice(1, 3), 16);
  const g = parseInt(backgroundColor.slice(3, 5), 16);
  const b = parseInt(backgroundColor.slice(5, 7), 16);
  
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return theme colors instead of hardcoded black/white
  return luminance > 0.5 ? colors.grey[900] : colors.grey[100];
};

/**
 * Get task border color using theme tokens
 * @param {Object} task - The task object
 * @param {string} mode - Theme mode ('light' or 'dark')
 * @returns {string} - Hex color code for border
 */
export const getTaskBorderColor = (task, mode = 'light') => {
  const colors = tokens(mode);
  
  if (isTaskOverdue(task)) {
    return colors.redAccent[700]; // Darker red for border
  }
  
  const { state } = task;
  switch (state) {
    case 'COMPLETED':
      return colors.greenAccent[700];
    case 'NOT_COMPLETED':
      return colors.yellowAccent[700];
    case 'IN_PROGRESS':
      return colors.yellowAccent[700];
    case 'PENDING':
    case 'SCHEDULED':
      return colors.grey[700];
    case 'ON_HOLD':
      return colors.pinkAccent[700];
    case 'CANCELLED':
      return colors.grey[800];
    default:
      return colors.grey[700];
  }
};

/**
 * Get task colors object for easy use in components
 * @param {Object} task - The task object
 * @param {string} mode - Theme mode ('light' or 'dark')
 * @returns {Object} - Object with background, text, border, and hover colors
 */
export const getTaskColors = (task, mode = 'light') => {
  return {
    background: getTaskColor(task, mode),
    text: getTaskTextColor(task, mode),
    border: getTaskBorderColor(task, mode),
    hover: getTaskColorVariant(task, mode, 'lighter'),
    hoverDark: getTaskColorVariant(task, mode, 'darker'),
  };
};

/**
 * Get priority colors using theme tokens
 * @param {string} priority - Priority level
 * @param {string} mode - Theme mode ('light' or 'dark')
 * @returns {string} - Hex color code
 */
export const getPriorityColor = (priority, mode = 'light') => {
  const colors = tokens(mode);
  
  switch (priority) {
    case 'LOW':
      return "success";
    case 'MEDIUM':
      return "warning";
    case 'HIGH':
      return "error";
    case 'CRITICAL':
      return "secondary";
    default:
      return "warning";
  }
};

/**
 * Actions that require confirmation before execution
 */
export const getActionsRequiringConfirmation = () => {
  const t = i18n.t.bind(i18n);
  
  return {
    complete: {
      title: t('tasks:dialogs.complete_task.title'),
      message: t('tasks:dialogs.complete_task.subtitle'),
      confirmText: t('tasks:dialogs.complete_task.confirm'),
      severity: 'success',
      notesField: {
        required: false,
        label: t('tasks:dialogs.complete_task.label'),
        placeholder: t('tasks:dialogs.complete_task.placeholder'),
      },
      // Only completion may record a different date, and only with admin approval
      allowBackdate: true,
    },
    not_complete: {
      title: t('tasks:dialogs.not_complete_task.title'),
      message: t('tasks:dialogs.not_complete_task.subtitle'),
      confirmText: t('tasks:dialogs.not_complete_task.confirm'),
      severity: 'warning',
      notesField: {
        required: true,
        label: t('tasks:dialogs.not_complete_task.label'),
        placeholder: t('tasks:dialogs.not_complete_task.placeholder'),
      },
    },
    cancel: {
      title: t('tasks:actions.cancel_task'),
      message: t('tasks:dialogs.confirm_action.cancel_task_message'),
      confirmText: t('tasks:actions.cancel_task'),
      severity: 'error',
    },
    reopen: {
      title: t('tasks:actions.reopen_task'),
      message: t('tasks:dialogs.confirm_action.reopen_task_message'),
      confirmText: t('tasks:actions.reopen_task'),
      severity: 'warning',
    },
  };
};

/**
 * Check if a task action requires user confirmation
 */
export const requiresConfirmation = (action) => {
  const actions = getActionsRequiringConfirmation();
  return action in actions;
};

/**
 * Get confirmation config for an action
 */
export const getConfirmationConfig = (action) => {
  const actions = getActionsRequiringConfirmation();
  return actions[action];
};

/**
 * Execute a task state change action using the provided API functions
 */
export const executeTaskStateChange = async (taskId, action, taskAPI) => {
  const {
    startTask,
    putTaskOnHold,
    resumeTask,
    completeTask,
    markTaskNotCompleted,
    cancelTask,
    reopenTask,
  } = taskAPI;

  switch (action) {
    case 'start':
      return await startTask(taskId);
    case 'hold':
      return await putTaskOnHold(taskId);
    case 'resume_pending':
      return await resumeTask(taskId, { start_immediately: false });
    case 'resume_progress':
      return await resumeTask(taskId, { start_immediately: true });
    case 'complete':
      return await completeTask(taskId);
    case 'not_complete':
      return await markTaskNotCompleted(taskId);
    case 'cancel':
      return await cancelTask(taskId);
    case 'reopen':
      return await reopenTask(taskId);
    default:
      return { success: false, error: 'Invalid action' };
  }
};

/**
 * Get the past tense form of an action for success messages
 */
export const getActionPastTense = (action) => {
  const t = i18n.t.bind(i18n);
  
  const pastTenseMap = {
    start: t('tasks:state_actions.started'),
    hold: t('tasks:state_actions.put_on_hold'),
    resume_pending: t('tasks:state_actions.resumed'),
    resume_progress: t('tasks:state_actions.resumed_and_started'),
    complete: t('tasks:state_actions.completed'),
    not_complete: t('tasks:state_actions.marked_not_completed'),
    cancel: t('tasks:state_actions.cancelled'),
    reopen: t('tasks:state_actions.reopened'),
  };
  
  return pastTenseMap[action] || `${action}ed`;
};

/**
 * Check if a task is overdue (only for active states, not SCHEDULED)
 */
export const isTaskOverdue = (task) => {
  if (task.state === 'SCHEDULED' || !task.due_date || ['COMPLETED', 'NOT_COMPLETED', 'CANCELLED'].includes(task.state)) {
    return false;
  }

  const dueDate = new Date(task.due_date);
  const today = new Date();

  // Compare only the date part (set time to midnight for both)
  dueDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return dueDate < today;
};